import React, { useState, useRef } from "react";
import { FaPause, FaPlay } from "react-icons/fa";
import {
  FaClockRotateLeft,
  FaMoneyBillTransfer,
  FaForward,
  FaBackward,
} from "react-icons/fa6";
import { HiPhone } from "react-icons/hi2";
import { TbSquareHalf } from "react-icons/tb";
import { useStopwatch } from "react-timer-hook";
import ReactPlayer from "react-player";
import textSizeBasedOnLength from "../../funcs/textResizer";
import { truncateText } from "../../utils/textUtils";
import TextModal from "../modals/TextModal";

const SJAnswer = ({
  game,
  ads,
  question,
  setShowAnswer,
  setShowWhoAnswered,
}) => {
  const [fullScreen, setFullScreen] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const { seconds, minutes, isRunning, start, pause, reset } = useStopwatch({
    autoStart: true,
  });

  // Audio player state
  const audioRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPosition, setAudioPosition] = useState(0);
  //eslint-disable-next-line
  const [audioLoaded, setAudioLoaded] = useState(false);

  const modeName = game?.modes?.find((mode) =>
    mode.questions.find(
      (q) => (q?.question?._id || q?.customQuestion?._id) === question?._id
    )
  )?.name;

  const answerText = question?.answer || "";
  const { truncated: truncatedAnswer, needsTruncation } = truncateText(
    answerText,
    120
  );

  // Audio player functions
  const handleAudioPlayPause = () => {
    setIsAudioPlaying(!isAudioPlaying);
  };

  const handleAudioSkipForward = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.getCurrentTime();
      audioRef.current.seekTo(currentTime + 15);
    }
  };

  const handleAudioSkipBackward = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.getCurrentTime();
      audioRef.current.seekTo(Math.max(0, currentTime - 15));
    }
  };

  const handleAudioProgress = (state) => {
    setAudioPosition(state.playedSeconds);
  };

  const handleAudioDuration = (duration) => {
    setAudioDuration(duration);
    setAudioLoaded(true);
  };

  const handleAudioSeek = (e) => {
    const seekValue = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.seekTo(seekValue);
    }
    setAudioPosition(seekValue);
  };

  // Format time in mm:ss
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "0:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <>
      <div className="w-full h-[90vh] flex flex-col portrait:flex-col sm:flex-row portrait:py-10 pl-10 pr-2 py-10 lg:py-20">
        <div
          className={`portrait:w-full portrait:h-3/4 relative h-3/4 w-full sm:w-5/6 sm:h-full ${textSizeBasedOnLength(
            question?.answer
          )} font-bold text-dark border-[6px] border-[#0000CD] rounded-[50px] pt-4 sm:pt-6 md:pt-8`}
        >
          <div className="h-2/6 md:h-1/6 w-full flex flex-col justify-center items-center p-2 px-4 md:px-10">
            <div className="flex-1 flex items-center justify-center">
              <h1
                style={{ direction: "rtl", textAlign: "right" }}
                className="leading-relaxed"
              >
                {truncatedAnswer}
              </h1>
            </div>
            {needsTruncation && (
              <button
                onClick={() => setShowTextModal(true)}
                className="mt-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors"
              >
                عرض أكثر
              </button>
            )}
          </div>
          <div className="h-4/6 md:h-5/6 w-full flex justify-center items-start px-4 pb-4 md:p-10">
            {question?.answerVideo && question?.answerVideo.length > 10 ? (
              <ReactPlayer
                url={question?.answerVideo}
                controls
                width="100%"
                height="100%"
              />
            ) : question?.answerAudio && question?.answerAudio.length > 10 ? (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <div className="w-full max-w-md bg-gray-100 p-6 rounded-xl shadow-md">
                  {/* Audio Player - Custom UI */}
                  <div className="flex flex-col items-center gap-5">
                    {/* Play Controls */}
                    <div className="flex items-center justify-center gap-8">
                      <button
                        onClick={handleAudioSkipBackward}
                        className="text-dark hover:text-gray-700"
                      >
                        <FaBackward size={24} />
                      </button>

                      <button
                        onClick={handleAudioPlayPause}
                        className="text-dark hover:text-gray-700 bg-gray-200 rounded-full p-3"
                      >
                        {isAudioPlaying ? (
                          <FaPause size={32} />
                        ) : (
                          <FaPlay size={32} />
                        )}
                      </button>

                      <button
                        onClick={handleAudioSkipForward}
                        className="text-dark hover:text-gray-700"
                      >
                        <FaForward size={24} />
                      </button>
                    </div>

                    {/* Time display */}
                    <div className="text-dark text-center mt-2">
                      {formatTime(audioPosition)} / {formatTime(audioDuration)}
                    </div>

                    {/* Progress slider */}
                    <div className="w-full mt-2">
                      <input
                        type="range"
                        min={0}
                        max={audioDuration || 100}
                        value={audioPosition || 0}
                        onChange={handleAudioSeek}
                        className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Hidden actual player for controlling audio */}
                  <div className="hidden">
                    <ReactPlayer
                      ref={audioRef}
                      url={question?.answerAudio}
                      width="0"
                      height="0"
                      playing={isAudioPlaying}
                      onProgress={handleAudioProgress}
                      onDuration={handleAudioDuration}
                      onEnded={() => setIsAudioPlaying(false)}
                      config={{
                        file: {
                          forceAudio: true,
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setFullScreen(true);
                }}
                className="h-full"
              >
                <img
                  className="h-full object-contain"
                  src={question?.answerImage || question?.questionImage}
                  alt={question?.answer}
                />
              </button>
            )}
          </div>
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2/3 w-11/12 sm:w-full flex flex-row justify-between items-center bg-transparent">
            {/* Left Section */}
            <div className="w-1/3 flex justify-center items-center">
              <div
                className={`hidden sm:block border-[#2a2634] border-4 rounded-2xl ${
                  question?.spare ? "bg-behindButton" : ""
                } ${question?.custom ? "bg-mainText" : ""}`}
              >
                {question?.custom && (
                  <div className="text-center px-2 text-dark rounded-lg text-sm lg:text-xl">
                    فئة خاصة
                  </div>
                )}
                {question?.spare && (
                  <div className="text-center px-2 text-white rounded-lg text-sm lg:text-xl">
                    وسيلة مساعدة
                  </div>
                )}
                <div className="text-center p-1 px-4 lg:p-2 bg-[#2a2634] text-white rounded-lg text-sm lg:text-xl ">
                  {question?.spare
                    ? game?.teams?.find((team) => team.currentTurn).name
                    : modeName}
                </div>
              </div>
            </div>

            {/* Center Section (Timer Controls) */}
            <div className="w-6/12 flex justify-center items-center">
              <div className="p-2 px-8  bg-[#2a2634] text-white rounded-full flex gap-14 text-lg lg:text-3xl">
                <button onClick={() => (isRunning ? pause() : start())}>
                  {isRunning ? <FaPause /> : <FaPlay />}
                </button>
                <h1 className="text-center">
                  {minutes < 10 && "0"}
                  {minutes}:{seconds < 10 && "0"}
                  {seconds}
                </h1>
                <button onClick={() => reset()}>
                  <FaClockRotateLeft />
                </button>
              </div>
            </div>

            {/* Right Section */}
            <div className="w-1/3 flex justify-center items-center">
              <div className="hidden sm:block border-[#2a2634] border-4 rounded-2xl">
                <div className="text-right p-1 px-4 lg:p-2 bg-[#2a2634] text-white rounded-lg text-sm lg:text-xl flex gap-1">
                  <div>نقطة</div>
                  <div>{question?.points}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0   transform translate-x-1/2 translate-y-1/2 ">
            <button
              className="bg-red-700 rounded-full hover:bg-red-800 text-white font-bold py-3 px-4 md:px-10 text-sm md:text-xl"
              onClick={() => {
                setShowWhoAnswered(true);
              }}
            >
              منو جاوب
            </button>
          </div>
          <div className="absolute  bottom-0   transform -translate-x-1/2 translate-y-1/2 right-0  text-white rounded-lg  text-sm lg:text-xl ">
            <button
              className="bg-green-700 rounded-full hover:bg-green-800 text-white font-bold py-3 px-4 md:px-10 text-sm md:text-xl"
              onClick={() => {
                setShowAnswer(false);
              }}
            >
              السؤال
            </button>
          </div>
        </div>
        <div className="portrait:h-1/4 portrait:w-full h-1/4 w-full sm:w-1/4 sm:h-full flex-row sm:flex-col portrait:flex-row flex justify-center items-center text-white text-4xl sm:pl-4 sm:gap-2 portrait:pt-6 pt-6 sm:pt-0">
          {game?.teams.map((team, index) => (
            <div
              className="w-1/4 h-full sm:w-full sm:h-1/4 px-[1px] lg:p-2"
              key={index}
            >
              <div
                className={`portrait:flex-col w-full h-full flex flex-col sm:flex-row justify-center items-center`}
              >
                <div className="h-1/2 w-full sm:w-2/3 sm:h-full text-sm md:text-lg lg:text-xl font-bold ">
                  <div
                    className={`h-1/2 flex items-center justify-center overflow-hidden bg-[#00009e] rounded-full  ${
                      team.currentTurn === true && "border-4 border-gray-400"
                    }`}
                  >
                    <h1 className="text-white text-center">{team?.name}</h1>
                  </div>
                  <div className="portrait:w-full portrait:h-1/2 w-full h-1/2 flex justify-center items-center p-1 gap-1 lg:gap-3">
                    {team?.powerUps.map((powerUp, index) => (
                      <button
                        disabled
                        className="rounded-full border-[4px] border-[#00009e] text-[#00009e]   p-1   lg:p-2  text-sm  md:text-lg xl:text-xl disabled:border-gray-400  disabled:text-gray-400"
                        key={index}
                      >
                        {powerUp?.name === "one" ? (
                          <FaMoneyBillTransfer />
                        ) : powerUp?.name === "two" ? (
                          <HiPhone />
                        ) : (
                          <TbSquareHalf />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {fullScreen && (
        <button
          onClick={() => {
            setFullScreen(false);
          }}
          className="absolute h-[100vh] w-[100vw] bg-[#00000090]"
        >
          {question?.answerVideo && question?.answerVideo.length > 10 ? (
            <ReactPlayer
              url={question?.answerVideo}
              controls
              width="100%"
              height="100%"
              playing={true}
              className="h-full w-full object-contain"
            />
          ) : question?.answerAudio && question?.answerAudio.length > 10 ? (
            <div className="w-full h-full flex flex-col justify-center items-center">
              <div className="w-full max-w-md bg-gray-100 p-6 rounded-xl shadow-md">
                <ReactPlayer
                  url={question?.answerAudio}
                  controls
                  width="100%"
                  height="50px"
                  playing={true}
                  config={{
                    file: {
                      forceAudio: true,
                    },
                  }}
                />
              </div>
            </div>
          ) : (
            <img
              src={question?.answerImage}
              alt={question?.answer}
              className="h-full w-full object-contain"
            />
          )}
        </button>
      )}
      {/* Text Modal */}
      <TextModal
        isOpen={showTextModal}
        onClose={() => setShowTextModal(false)}
        text={answerText}
        title="نص الإجابة"
      />
    </>
  );
};

export default SJAnswer;
